// =============================================================================
// VandieZ admin-web -- Identity change review (/kyc/review-flags)
// =============================================================================
// D-20. Separate from the KYC queue on purpose:
//
//   /kyc/queue         "nobody has ever approved this account"
//   /kyc/review-flags  "somebody approved it, and then the identity moved"
//
// They need different evidence on screen and a different decision at the
// end, and merging them would bury a handful of flags under every new
// signup.
//
// THE ACCOUNT IS STILL TRADING while a flag is open — that is the whole
// point of the decision (see the migration). The common case is someone
// fixing a typo in their own GSTIN, and freezing a paying customer
// mid-trip over a one-character edit is worse than the fraud it
// prevents. So this queue is a review backlog, not an outage: oldest
// first, because an unreviewed identity has been trading for that whole
// time.
// =============================================================================

import { AlertCircle, RefreshCw, ShieldAlert } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { kycReviewFlagsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { useToast } from "@/lib/toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type { KycReviewFlagRow } from "@/types/kyc_review_flags";

const PAGE_SIZE = 50;

function formatDateTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function fieldLabel(field: string): string {
  return field.replace(/_/g, " ");
}

export function KycReviewFlagsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState<KycReviewFlagRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [openOnly, setOpenOnly] = useState<boolean>(true);
  // Note per flag, not one shared box: an admin working three flags
  // should not have the reason for one leak into another.
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await kycReviewFlagsAdminApi.list(
          accessToken,
          { openOnly, limit: PAGE_SIZE, offset: 0 },
          signal,
        );
        setRows(res.items);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        setError(
          e instanceof ApiError ? e.problem.detail : "Unexpected error.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, openOnly],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  const resolve = async (
    row: KycReviewFlagRow,
    resolution: "reaffirmed" | "downgraded",
  ): Promise<void> => {
    if (accessToken === null) {
      return;
    }
    const note = (notes[row.id] ?? "").trim();
    // Checked here as well as server-side so the admin sees it next to
    // the box rather than as a 422 toast.
    if (resolution === "downgraded" && note.length < 4) {
      setError(
        "A downgrade needs a note — the user will ask why they lost their tier.",
      );
      return;
    }
    setBusyId(row.id);
    setError(null);
    try {
      await kycReviewFlagsAdminApi.resolve(accessToken, row.id, {
        resolution,
        ...(note.length > 0 ? { note } : {}),
      });
      toast.success(
        resolution === "reaffirmed"
          ? `Kept ${row.full_name ?? "this account"} verified.`
          : `Sent ${row.full_name ?? "this account"} back for re-KYC.`,
      );
      await load();
    } catch (e: unknown) {
      const detail =
        e instanceof ApiError ? e.problem.detail : "Could not resolve.";
      setError(detail);
      toast.error(detail);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Identity changes"
        description="Accounts that changed a KYC-bearing field AFTER being approved. They are still trading — this is a review backlog, not a block."
        actions={
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setOpenOnly((v) => !v);
              }}
            >
              {openOnly ? "Show resolved too" : "Show open only"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void load();
              }}
            >
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
          </div>
        }
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>
            <ShieldAlert className="mr-1 inline h-4 w-4" />
            {openOnly ? "Open" : "All"} flags ({rows.length})
          </CardTitle>
          <CardDescription>
            Compare the old and new value against the documents on file.
            <strong> Reaffirm</strong> keeps the tier — the right call for a
            corrected typo. <strong>Downgrade</strong> returns the account to
            document_pending, which stops them posting and bidding until they
            re-verify.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && rows.length === 0 ? (
            <TableSkeleton columns={5} />
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">
              {openOnly
                ? "Nothing waiting — no approved account has changed its identity."
                : "No identity changes recorded yet."}
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className="rounded-lg border p-4 text-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <Link
                        to={`/users/${r.user_id}`}
                        className="font-medium underline-offset-2 hover:underline"
                      >
                        {r.full_name ?? "(no name)"}
                      </Link>
                      <div className="text-xs text-muted-foreground">
                        {r.phone_masked ?? "—"}
                        {r.primary_role !== null && <> · {r.primary_role}</>}
                        {" · raised "}
                        {formatDateTime(r.raised_at)}
                      </div>
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      <div>
                        approved as{" "}
                        <span className="font-mono">
                          {r.kyc_tier_at_change ?? "—"}
                        </span>
                      </div>
                      {/* The tier NOW, so a reviewer can see whether
                          something has already been done since. */}
                      <div>
                        now{" "}
                        <span className="font-mono">
                          {r.current_kyc_tier ?? "—"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 rounded bg-muted/50 p-3">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      {fieldLabel(r.field_name)}
                    </div>
                    <div className="mt-1 font-mono text-xs">
                      <span className="line-through opacity-60">
                        {r.old_value ?? "(unset)"}
                      </span>
                      {"  →  "}
                      <span className="font-semibold">
                        {r.new_value ?? "(cleared)"}
                      </span>
                    </div>
                  </div>

                  {r.resolved_at === null ? (
                    <div className="mt-3 space-y-2">
                      <Textarea
                        value={notes[r.id] ?? ""}
                        onChange={(e) => {
                          setNotes((prev) => ({
                            ...prev,
                            [r.id]: e.target.value,
                          }));
                        }}
                        placeholder="What did you check? (required to downgrade)"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          disabled={busyId === r.id}
                          onClick={() => {
                            void resolve(r, "reaffirmed");
                          }}
                        >
                          {busyId === r.id ? "Working…" : "Reaffirm"}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={
                            busyId === r.id ||
                            (notes[r.id] ?? "").trim().length < 4
                          }
                          onClick={() => {
                            void resolve(r, "downgraded");
                          }}
                        >
                          Downgrade to re-KYC
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-muted-foreground">
                      {r.resolution} · {formatDateTime(r.resolved_at)}
                      {r.resolution_note !== null && (
                        <> · {r.resolution_note}</>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
